import React, { useState } from 'react';
import ViewTable from 'app/shared/view-table/view-table';
import ViewTableHeaderColumn from 'app/shared/view-table/view-table-header-column';
import { convertDateFromServer } from 'app/shared/util/date-utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router-dom';
import {
  copyMainProjectDocumentQuery,
  deleteMainProjectDocumentQuery,
  fetchMainProjectDocumentsTableQuery,
} from 'app/edozvola/cases/case-main-project-documents/service/case-main-project-documents.queries';
import { MainProjectDocumentView } from 'app/edozvola/cases/case-main-project-documents/model/case-main-project-documents.model';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Button } from 'edozvola-frontend-lib/dist/ui/components';
import { AttachmentModalVariant } from 'app/edozvola/cases/project-documents/attachment-modal/attachment-modal';
import { TABLE_QUERY_KEYS } from 'app/config/constants';
import { getInvalidRowsForTable } from 'app/shared/util/validation-errors.util';
import { getCaseContext } from 'app/shared/util/context-util';
import { TooltipWrapper } from 'edozvola-frontend-lib/dist/ui/components/tooltipwrapper';

const CaseMainProjectDocumentsTable = ({
  setMainProjectDocument,
  setReadOnly,
  setMainProjectDocumentForAttachments,
  setMainProjectAttachmentVariant,
}) => {
  const { id } = useParams<{ id: string }>();
  const { sidebarItemPair } = getCaseContext();

  const invalidRowIds = getInvalidRowsForTable(TABLE_QUERY_KEYS.MAIN_PROJECT_DOCUMENTS_TABLE);

  const [idProjectDocumentToDelete, setIdProjectDocumentToDelete] = useState();
  const [deleteModalShown, setDeleteModalShown] = useState(false);

  const { mutate: deleteMainProjectDocumentMutation, isPending: isDeletingProjectDocument } = deleteMainProjectDocumentQuery();
  const { mutate: copyMainProjectDocumentMutation, isPending: isCopyingProjectDocument } = copyMainProjectDocumentQuery();


  const openMainProjectDocumentForm = (idMainProjectDocument: number, readOnly: boolean) => {
    setMainProjectDocument(idMainProjectDocument);
    setReadOnly(readOnly);
  };

  const openDeleteDialog = idToDelete => {
    setIdProjectDocumentToDelete(idToDelete);
    setDeleteModalShown(true);
  };

  const removeMainProjectDocument = () => {
    setDeleteModalShown(false);
    deleteMainProjectDocumentMutation(idProjectDocumentToDelete);
  };

  const openAttachmentModal = (value: MainProjectDocumentView, type: AttachmentModalVariant) => {
    setMainProjectDocumentForAttachments(value.idMainProjectDocument);
    setMainProjectAttachmentVariant(type);
  };

  const renderTableRow = (value: MainProjectDocumentView, projectDocumentIndex: number, totalItems: number) => {
    return (
      <tr key={projectDocumentIndex} className={invalidRowIds.includes(value.idMainProjectDocument) ? 'invalid-table-row' : ''}>
        <td>
          <div className={'column-with-multiple-data'}>
            <span>{value.projectTypeByVocationalDesignation}</span>
            <span>{value.additionalDescription}</span>
          </div>
        </td>
        <td>
          <div className={'column-with-multiple-data'}>
            <span>{value.authorizedDesigner}</span>
            <span>{value.designerOfficeAddress}</span>
          </div>
        </td>
        <td>{value.projectMap}</td>
        <td>
          <div className={'column-with-multiple-data'}>
            <span>{value.projectMark}</span>
            <span>{convertDateFromServer(value.projectCreatedDate)}</span>
          </div>
        </td>

        <td>
          <div className={'actions-container'}>
            {sidebarItemPair?.canAddAttachment && (
              <TooltipWrapper message={'Dodaj prilog'}>
                <FontAwesomeIcon icon={'plus'} className={'icon-table'} onClick={() => openAttachmentModal(value, 'ADD')} />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canViewAttachments && value.hasAttachments && (
              <TooltipWrapper message={'Pregledaj priloge'}>
                <FontAwesomeIcon icon={'eye'} className={'icon-table'} onClick={() => openAttachmentModal(value, 'VIEW')} />
              </TooltipWrapper>
            )}
          </div>
        </td>

        <td>
          <div className={'actions-container'}>
            {sidebarItemPair?.canView && (
              <TooltipWrapper message={'Pregledaj'}>
                <FontAwesomeIcon
                  icon={'eye'}
                  className={'icon-table'}
                  onClick={() => openMainProjectDocumentForm(value.idMainProjectDocument, true)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canEdit && value.canEdit && (
              <TooltipWrapper message={'Uredi'}>
                <FontAwesomeIcon
                  icon={'pen-to-square'}
                  className={'icon-table'}
                  onClick={() => openMainProjectDocumentForm(value.idMainProjectDocument, false)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canCopy && (
              <TooltipWrapper message={'Kopiraj'}>
                <FontAwesomeIcon
                  icon={'copy'}
                  className={'icon-table'}
                  onClick={() => copyMainProjectDocumentMutation(value.idMainProjectDocument)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canDelete && value.canDelete && (
              <TooltipWrapper message={'Obriši'}>
                <FontAwesomeIcon
                  icon={'trash-alt'}
                  className={'icon-table'}
                  onClick={() => openDeleteDialog(value.idMainProjectDocument)}
                />
              </TooltipWrapper>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <ViewTable
        initialPaginationColumn={'ordering'}
        renderTableRow={renderTableRow}
        fetchDataQuery={fetchMainProjectDocumentsTableQuery}
        fetchDataProps={id}
        noDataMessage={'Nema dodanih dijelova po strukovnim odrednicama.'}
      >
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Strukovna odrednica', 'Dodatni opis']} width={'17%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Ovlašteni projektant']} width={'45%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Mapa']} width={'10%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Oznaka', 'Datum izrade']} width={'8%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Prilozi']} width={'5%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Akcije']} />
      </ViewTable>

      <Modal scrollable isOpen={deleteModalShown} size={'lg'} toggle={() => setDeleteModalShown(false)} centered>
        <ModalHeader>
          <span>Brisanje projekta</span>
        </ModalHeader>
        <ModalBody>
          <div>Jeste li sigurni da želite obrisati podatke?</div>
        </ModalBody>
        <ModalFooter>
          <Button size={'lg'} label="Odustani" outline onClick={() => setDeleteModalShown(false)} color={'secondary'} />
          <Button size={'lg'} label="Obriši" onClick={removeMainProjectDocument} color={'danger'} />
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CaseMainProjectDocumentsTable;
